import { useCallback } from 'react';
import { useQuery, useMutation } from 'react-query';
import {
  getMealPlanCategories,
  getMealPlanListPerCategory,
  saveMealPlan,
  saveMealPlanBulk,
  getMealPlanDetail,
  updateMealPlan,
  deleteMealPlan,
  saveMealPlanImage,
  deleteMealPlanItems,
  updateMealPlanSort,
  getMealPlanSurveyStats
} from 'apis';
import { TMealPlanCategory, TMealPlans, TMealPlan, TSurveyStats, TMealPlanRequest } from 'interfaces/mealPlan';
import { useMeInfo } from './useMeInfo';
import { useUploadImage } from './useCommon';

// 식단표 카테고리 조회
export const useMealPlanCategories = () => {
  return useQuery<TMealPlanCategory[]>(['getMealPlanCategories'], getMealPlanCategories);
};

// 식단표 조회 (카테고리별)
export const useMealPlanListPerCategory = () => {
  return useMutation<TMealPlans[], unknown, string>(['getMealPlanListPerCategory'], (serveDate: string) => {
    return getMealPlanListPerCategory(serveDate);
  });
};

// 식단표 등록
export const useSaveMealPlan = () => {
  return useMutation(['saveMealPlan'], (params: TMealPlanRequest) => saveMealPlan(params));
};

// 식단표 bulk 록
export const useSaveMealPlanBulk = () => {
  return useMutation(['saveMealPlanBulk'], (params: unknown) => saveMealPlanBulk(params));
};

// 식단표 상세 조회
export const useMealPlanDetail = () => {
  return useMutation<TMealPlan, unknown, string>(['getMealPlanDetail'], (mealPlanIdx?: string) => getMealPlanDetail(mealPlanIdx));
};

// 식단표 수정
export const useUpdateMealPlan = () => {
  return useMutation(['updateMealPlan'], ({ mealPlanIdx, params }: { mealPlanIdx: string; params: TMealPlanRequest }) => {
    return updateMealPlan(mealPlanIdx, params);
  });
};

// 식단표 비활성화
export const useDeleteMealPlan = () => {
  return useMutation(['deleteMealPlan'], (mealPlanIdx: string) => deleteMealPlan(mealPlanIdx));
};

// 식단표 이미지 업로드
export const useSaveMealPlanImage = () => {
  return useMutation(
    ['saveMealPlanImage'],
    ({ mealPlanIdx, params }: { mealPlanIdx: number; params: { mealPlanImageUrl: string } }) => {
      return saveMealPlanImage(mealPlanIdx, params);
    }
  );
};

// 식단표 이미지 pre-signed 업로드 → mealPlanImageUrl
export const useUploadMealPlanImage = () => {
  const { selectedStore } = useMeInfo();
  const { mutateAsync: uploadImage } = useUploadImage();
  const storeId = selectedStore?.sid;
  return useCallback(
    (file: File) => uploadImage({ serviceName: 'store-meal-plan', storeId, file, fileName: file.name }),
    [storeId, uploadImage]
  );
};

// 식단표 식단 항목 비활성화
export const useDeleteMealPlanItems = () => {
  return useMutation(['deleteMealPlanItems'], (mealPlanItemIdx: number) => deleteMealPlanItems(mealPlanItemIdx));
};

// 식단표 순서 변경
export const useUpdateMealPlanSort = () => {
  return useMutation(['updateMealPlanSort'], ({ mealPlanIdx, params }: { mealPlanIdx: number; params: unknown }) => {
    return updateMealPlanSort(mealPlanIdx, params);
  });
};

// 식단표 설문조사 통계
export const useGetMealPlanSurveyStats = (params: { startDate: string; endDate: string }) => {
  return useQuery<TSurveyStats>(
    ['getMealPlanSurveyStats', params.startDate, params.endDate],
    () => getMealPlanSurveyStats(params),
    { enabled: !!params.startDate && !!params.endDate }
  );
};
